import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';

interface TradeCard {
  type: 'unusual' | 'whale';
  market: string;
  amount: string;
  price: string;
  change: string;
  isPositive: boolean;
  time: string;
}

type MockTab = 'whale' | 'leaderboard' | 'heatmap';

type OsStyle = 'mac' | 'windows' | 'linux' | 'unknown';

const Hero: React.FC = () => {
  const [stats, setStats] = useState({ volume: 0, markets: 0 });
  const [activeTab, setActiveTab] = useState<MockTab>('whale');
  const [osStyle, setOsStyle] = useState<OsStyle>('unknown');

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };
  const [tradeCards] = useState<TradeCard[]>([
    {
      type: 'whale',
      market: 'Bitcoin Up or Down - July 12, 12AM ET',
      amount: '$1,847',
      price: '78.2¢',
      change: '+2.1%',
      isPositive: true,
      time: '1m ago'
    },
    {
      type: 'unusual',
      market: 'Will Trump win 2024 Election?',
      amount: '$3,245',
      price: '63.5¢',
      change: '-1.2%',
      isPositive: false,
      time: '3m ago'
    }
  ]);

  useEffect(() => {
    // Animate stats
    const animateStats = () => {
      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;
      
      let step = 0;
      const timer = setInterval(() => {
        const progress = step / steps;
        setStats({
          volume: Math.floor(20000000 * progress),
          markets: Math.floor(500 * progress)
        });
        
        step++;
        if (step > steps) clearInterval(timer);
      }, interval);
    };

    animateStats();
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('mac os')) {
      setOsStyle('mac');
    } else if (ua.includes('windows')) {
      setOsStyle('windows');
    } else if (ua.includes('linux')) {
      setOsStyle('linux');
    } else {
      setOsStyle('unknown');
    }
  }, []);

  const formatVolume = (num: number) => {
    return `$${(num / 1000000).toFixed(1)}M+`;
  };

  const mockRows = useMemo(() => {
    if (activeTab === 'leaderboard') {
      return [
        { market: 'Top Wallet: @FlowWinner', amount: '$124k 7d PnL' },
        { market: 'Top Wallet: @EventAlpha', amount: '$97k 7d PnL' },
        { market: 'Top Wallet: @VolSurfer', amount: '$65k 7d PnL' }
      ];
    }
    if (activeTab === 'heatmap') {
      return [
        { market: 'Elections Heat ↑', amount: '+23% vol' },
        { market: 'Crypto Heat ↑', amount: '+18% vol' },
        { market: 'Sports Heat ↓', amount: '-6% vol' }
      ];
    }
    // whale (default)
    return [
      { market: '🥊 UFC 319: Du Plessis vs. Chimaev', amount: '$2,464' },
      { market: '🏛️ Will the Supreme Court overturn...', amount: '$1,329' },
      { market: '₿ Bitcoin Up or Down - July 12', amount: '$1,194' }
    ];
  }, [activeTab]);

  return (
    <section className="hero">
      <div className="hero-container">
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ 
            duration: 0.8,
            ease: [0.25, 0.25, 0.25, 0.75]
          }}
        >
          <h1 className="hero-title">
            Get the <span className="highlight">Flow</span> on Polymarket
          </h1>
          <p className="hero-subtitle">
            Track unusual activity, monitor big trades, and spot market movements before they happen. 
            Professional-grade intelligence for Polymarket traders.
          </p>
          <div className="hero-buttons">
            <motion.a 
              href="https://app.prediedge.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-large"
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2, ease: "easeOut" }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
            >
              Launch App
            </motion.a>
            <motion.button 
              className="btn btn-secondary btn-large"
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2, ease: "easeOut" }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
              onClick={() => scrollToSection('features')}
            >
              View Features
            </motion.button>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">{formatVolume(stats.volume)}</span>
              <span className="stat-label">Volume Tracked Daily</span>
            </div>
            <div className="stat">
              <span className="stat-number">{stats.markets}+</span>
              <span className="stat-label">Active Markets</span>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          className="hero-visual"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ 
            duration: 0.8, 
            delay: 0.2,
            ease: [0.25, 0.25, 0.25, 0.75]
          }}
        >
          <div className="product-showcase">
            <div className="product-image-placeholder">
              <div className="platform-screenshot">
                <div className={`screenshot-header ${osStyle === 'windows' ? 'win' : 'mac'}`}>
                  <div className={`window-controls ${osStyle === 'windows' ? 'win' : ''}`}>
                    {osStyle === 'windows' ? (
                      <>
                        <div className="control win minimize"></div>
                        <div className="control win maximize"></div>
                        <div className="control win close"></div>
                      </>
                    ) : (
                      <>
                        <div className="control red"></div>
                        <div className="control yellow"></div>
                        <div className="control green"></div>
                      </>
                    )}
                  </div>
                  <div className="url-bar">app.prediedge.com</div>
                </div>
                <div className="screenshot-content">
                  <div className="mock-nav">
                    <button className={`nav-item ${activeTab === 'whale' ? 'active' : ''}`} onClick={() => setActiveTab('whale')}>
                      🐋 Whale Tape
                    </button>
                    <button className={`nav-item ${activeTab === 'leaderboard' ? 'active' : ''}`} onClick={() => setActiveTab('leaderboard')}>
                      📊 Leaderboard
                    </button>
                    <button className={`nav-item ${activeTab === 'heatmap' ? 'active' : ''}`} onClick={() => setActiveTab('heatmap')}>
                      🔥 Heat Map
                    </button>
                  </div>
                  <div className="mock-feed">
                    {mockRows.map((row, idx) => (
                      <div className="mock-trade-row" key={idx}>
                        <div className="mock-market">{row.market}</div>
                        <div className="mock-amount">{row.amount}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="floating-cards">
              {tradeCards.map((card, index) => (
                <motion.div
                  key={index}
                  className={`trade-card ${index === 0 ? 'card-1' : 'card-2'}`}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ 
                    duration: 8, 
                    repeat: Infinity,
                    delay: index * 4,
                    ease: "easeInOut"
                  }}
                >
                  <div className="trade-header">
                    <span className={`trade-type ${card.type}`}>
                      {card.type === 'unusual' ? 'Unusual Activity' : 'Large Trade'}
                    </span>
                    <span className="trade-time">{card.time}</span>
                  </div>
                  <div className="trade-details">
                    <h4>{card.market}</h4>
                    <p className="trade-amount">{card.amount} @ {card.price}</p>
                    <span className={`trade-change ${card.isPositive ? 'positive' : 'negative'}`}>
                      {card.change}
                    </span>
                  </div>
                  <div className="trader-info">
                    <span className="trader-name">
                      {index === 0 ? '@FlowWinner' : '@MarketMover'}
                    </span>
                    <span className="trade-action">
                      {card.type === 'whale' ? 'BUY' : 'SELL'}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero; 